import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, CalendarDays, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

interface CalendarSectionProps {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabledDays: (date: Date) => boolean;
  onMonthChange?: (date: Date) => void;
  month?: Date;
  isDatesLoading?: boolean;
}

export const CalendarSection: React.FC<CalendarSectionProps> = ({
  selectedDate,
  onDateChange,
  disabledDays,
  onMonthChange,
  month,
  isDatesLoading = false
}) => {
  const handleMonthChange = (date: Date) => {
    if (onMonthChange) {
      onMonthChange(date);
    }
  };

  return (
    <div>
      <div className="flex items-center mb-2">
        <CalendarDays className="w-4 h-4 mr-2 text-[#00B6F0]" />
        <h3 className="text-sm font-medium">
          Selecione uma data
          {isDatesLoading && (
            <span className="ml-2 text-xs text-gray-400 flex items-center">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Carregando disponibilidade...
            </span>
          )}
        </h3>
      </div>
      
      <div className="relative">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onDateChange}
          disabled={disabledDays}
          locale={ptBR}
          className="rounded-md border border-[#223342] bg-[#1A2233]"
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center text-white",
            caption_label: "text-sm font-medium text-white",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-transparent p-0 text-gray-400 hover:text-white",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-gray-400 rounded-md w-9 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-[#00B6F0]/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-normal text-white hover:bg-[#223342] hover:text-white rounded-md",
            day_selected: "bg-[#00B6F0] text-black hover:bg-[#00B6F0] hover:text-black focus:bg-[#00B6F0] focus:text-black",
            day_today: "bg-[#223342] text-white",
            day_outside: "text-gray-600",
            day_disabled: "text-gray-600 opacity-50 cursor-not-allowed",
            day_range_middle: "aria-selected:bg-[#00B6F0]/20 aria-selected:text-white",
            day_hidden: "invisible",
          }}
        />
        
        {/* Overlay de loading sobre o calendário */}
        {isDatesLoading && (
          <div className="absolute inset-0 bg-[#1A2233]/80 rounded-md flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-[#00B6F0] animate-spin mx-auto mb-2" />
              <p className="text-xs text-gray-400">Verificando disponibilidade...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
